import { doc, arrayUnion, arrayRemove, writeBatch, getDocs, query, collection, where } from 'firebase/firestore';

export const updateCollectionWriteAccess = async (
  db: any,
  collectionIds: string[],
  userId: string,
  action: 'grant' | 'revoke'
) => {
  if (!collectionIds.length) return;
  
  const batch = writeBatch(db);
  const arrayOperation = action === 'grant' ? arrayUnion(userId) : arrayRemove(userId);
  
  collectionIds.forEach(collectionId => {
    const collectionRef = doc(db, 'usersMyCollection', collectionId);
    batch.update(collectionRef, {
      writeAccess: arrayOperation
    });
  });
  
  await batch.commit();
};

export const syncCollectionWriteAccess = async (
  db: any,
  oldCollectionWrite: string[],
  newCollectionWrite: string[],
  userId: string
) => {
  const oldSet = new Set(oldCollectionWrite || []);
  const newSet = new Set(newCollectionWrite || []);
  
  // Find collections that were added (grant access)
  const collectionsToGrant = newCollectionWrite.filter(collectionId => !oldSet.has(collectionId));
  
  // Find collections that were removed (revoke access)
  const collectionsToRevoke = oldCollectionWrite.filter(collectionId => !newSet.has(collectionId));
  
  // Update collections in parallel
  const promises = [];

  console.log('Collections to grant:', collectionsToGrant);
  console.log('Collections to revoke:', collectionsToRevoke);
  
  if (collectionsToGrant.length > 0) {
    promises.push(updateCollectionWriteAccess(db, collectionsToGrant, userId, 'grant'));
  }
  
  if (collectionsToRevoke.length > 0) {
    promises.push(updateCollectionWriteAccess(db, collectionsToRevoke, userId, 'revoke'));
  }
  
  await Promise.all(promises);
};

// Helper function to get all collections for given project IDs
const getCollectionsForProjects = async (db: any, projectIds: string[]): Promise<string[]> => {
  if (projectIds.length === 0) return [];
  
  const allCollectionIds: string[] = [];
  
  // Firestore 'in' operator supports max 10 items, so batch the queries
  for (let i = 0; i < projectIds.length; i += 10) {
    const batch = projectIds.slice(i, i + 10);
    const collectionsQuery = query(
      collection(db, 'usersMyCollection'), 
      where('associatedProjectId', 'in', batch)
    );
    const collectionsSnapshot = await getDocs(collectionsQuery);
    const collectionIds = collectionsSnapshot.docs.map(doc => doc.id);
    allCollectionIds.push(...collectionIds);
  }
  
  return allCollectionIds;
};

export const syncProjectWriteAccessToCollections = async (
  db: any,
  oldProjectWrite: string[],
  newProjectWrite: string[],
  userId: string
) => {
  const oldSet = new Set(oldProjectWrite || []);
  const newSet = new Set(newProjectWrite || []);
  
  // Find projects that were added (grant access to all collections in these projects)
  const projectsToGrant = newProjectWrite.filter(projectId => !oldSet.has(projectId));
  
  // Find projects that were removed (revoke access from all collections in these projects)
  const projectsToRevoke = oldProjectWrite.filter(projectId => !newSet.has(projectId));
  
  const promises = [];
  
  // Grant access to collections in new projects
  if (projectsToGrant.length > 0) {
    promises.push(
      getCollectionsForProjects(db, projectsToGrant).then(collectionIds => {
        return collectionIds.length > 0 ? updateCollectionWriteAccess(db, collectionIds, userId, 'grant') : Promise.resolve();
      })
    );
  }
  
  // Revoke access from collections in removed projects
  if (projectsToRevoke.length > 0) {
    promises.push(
      getCollectionsForProjects(db, projectsToRevoke).then(collectionIds => {
        return collectionIds.length > 0 ? updateCollectionWriteAccess(db, collectionIds, userId, 'revoke') : Promise.resolve();
      })
    );
  }
  
  await Promise.all(promises);
};

